import React, { useState, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CallMergeOutlinedIcon from '@mui/icons-material/CallMergeOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

import Loading from '../Loading.jsx';
import HeaderContainer from '../HeaderContainer.jsx';
import Modal from '../Modal.jsx';
import LearnunitForm from '../../components/LearnUnits/LearnunitForm.jsx';
import MergeLearnunit from '../../components/LearnUnits/MergeLearnunit.jsx';
import Pagination from '../../components/Custom-UI/Tables/Pagination.jsx';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  TableButton,
} from '../../components/Custom-UI/Tables/Elements.jsx';
import {
  useGetPaginatedLearnunits,
  postLearnunit,
  putLearnunit,
  deleteLearnunit,
  getLearnunitErrorMessage,
} from '../../models/learnunits.js';

const isSuccess = response => response.status === 200 || response.status === 201;

// POST /learnunits/new devuelve [unidad, creada]; si ya existía una equivalente no crea otra.
const createLearnunit = async name => {
  const response = await postLearnunit({ name });
  if (!isSuccess(response)) {
    return getLearnunitErrorMessage(
      response,
      'Error al crear la unidad de aprendizaje, intente de nuevo.'
    );
  }
  if (response.data[1] === false) {
    return `Ya existe la unidad de aprendizaje «${response.data[0].name}».`;
  }
  return null;
};

const updateLearnunit = async (id, name) => {
  const response = await putLearnunit(id, { name });
  if (!isSuccess(response)) {
    return getLearnunitErrorMessage(
      response,
      'Error al editar la unidad de aprendizaje, intente de nuevo.'
    );
  }
  return null;
};

const LearnUnitsTable = () => {
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [page, setPage] = useState(1);
  const [learnunits, setLearnunits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [data, dataLoading] = useGetPaginatedLearnunits(page, itemsPerPage, searchFilter);
  const [pagination, setPagination] = useState({
    currentPage: page,
    itemsPerPage: itemsPerPage,
    totalPages: 0,
  });
  const [open, setOpen] = useState(false);
  const [modalContent, setModalContent] = useState();

  useEffect(() => {
    if (data && !dataLoading) {
      setLearnunits(data.learnunits || []);
      setPagination({
        currentPage: data.pagination?.currentPage || 1,
        itemsPerPage: itemsPerPage,
        totalPages: data.pagination?.totalPages || 0,
      });
      setLoading(false);
    }
  }, [data, dataLoading]);

  function handleSearch(option) {
    setSearchFilter(option.target.value);
    setPage(1);
    setLoading(true);
  }

  const handleDelete = async learnunit => {
    const confirmation = window.confirm(
      `¿Estás seguro que quieres eliminar la unidad de aprendizaje «${learnunit.name}»?`
    );
    if (confirmation) {
      const response = await deleteLearnunit(learnunit.id);
      if (response.status === 200 || response.status === 201) {
        alert('Unidad de aprendizaje eliminada con éxito.');
        window.location.reload();
      } else {
        alert(getLearnunitErrorMessage(response, 'Error al eliminar la unidad de aprendizaje.'));
      }
    }
  };

  return (
    <div className='antialiased font-sans'>
      <Modal
        open={open}
        setOpen={setOpen}
        sx={{ height: 'auto', maxHeight: '95vh', width: 600, maxWidth: '95vw', minWidth: 0 }}>
        {modalContent}
      </Modal>
      <div className='w-full divide-y divide-primary'>
        <HeaderContainer
          title='Unidades de aprendizaje'
          search={{ handleSearch: handleSearch }}
          actions={[
            {
              title: 'Añadir unidad',
              icon: <AddIcon />,
              handleClick: () => {
                setModalContent(
                  <LearnunitForm
                    title='Añadir unidad de aprendizaje'
                    onSave={createLearnunit}
                    successMessage='Unidad de aprendizaje creada correctamente.'
                    setOpen={setOpen}
                  />
                );
                setOpen(true);
              },
            },
          ]}
        />
        <div className='py-8 px-4 sm:px-8 min-w-full w-full'>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Unidad de aprendizaje</TableHeader>
                <TableHeader />
              </TableRow>
            </TableHead>
            <TableBody>
              {dataLoading || loading ? (
                <TableRow>
                  <TableCell colSpan='2'>
                    <div className='flex justify-center items-center h-full'>
                      <Loading />
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                learnunits.map(learnunit => (
                  <TableRow key={learnunit.id}>
                    <TableCell type='name'>{learnunit.name}</TableCell>
                    <TableCell>
                      <div className='flex gap-2 items-center justify-end'>
                        <TableButton
                          variant={'create'}
                          onClick={() => {
                            setModalContent(
                              <MergeLearnunit learnunit={learnunit} setOpen={setOpen} />
                            );
                            setOpen(true);
                          }}>
                          <CallMergeOutlinedIcon />
                        </TableButton>
                        <TableButton
                          variant={'edit'}
                          onClick={() => {
                            setModalContent(
                              <LearnunitForm
                                title='Editar unidad de aprendizaje'
                                initialName={learnunit.name || ''}
                                onSave={name => updateLearnunit(learnunit.id, name)}
                                successMessage='Unidad de aprendizaje actualizada correctamente.'
                                setOpen={setOpen}
                              />
                            );
                            setOpen(true);
                          }}>
                          <EditOutlinedIcon />
                        </TableButton>
                        <TableButton variant={'delete'} onClick={() => handleDelete(learnunit)}>
                          <DeleteOutlinedIcon color='red' />
                        </TableButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Pagination
            pagination={pagination}
            setPage={setPage}
            setLoading={setLoading}
            setItemPerPage={setItemsPerPage}
          />
        </div>
      </div>
    </div>
  );
};

export default LearnUnitsTable;
